import {
  Appointment,
  AppointmentStatus,
  AppointmentConfig,
  AppointmentSchedule,
  Menu,
} from "@ambii-com/core";
import { WhereFilterOp } from "firebase/firestore/lite";
import {
  liteCollections,
  liteGetDocument,
  liteQueryCollectionWith,
} from "~/utils/firestoreLite";

export async function readAppointmentConfig(
  appointmentConfigId: string
): Promise<AppointmentConfig> {
  return liteGetDocument(
    liteCollections.appointmentConfigs,
    appointmentConfigId
  ).then(AppointmentConfig.fromDoc);
}

export async function readAppointmentConfigByClinicId(
  clinicId: string
): Promise<AppointmentConfig | null> {
  const docs = await liteQueryCollectionWith(
    liteCollections.appointmentConfigs,
    ["clinicId", "==", clinicId]
  );
  return docs[0] ? AppointmentConfig.fromDoc(docs[0]) : null;
}

export async function readMenu(menuId: string): Promise<Menu> {
  return liteGetDocument(liteCollections.appointmentMenus, menuId).then(
    Menu.fromDoc
  );
}

export async function readMenusForClinic(clinicId: string): Promise<Menu[]> {
  const docs = await liteQueryCollectionWith(
    liteCollections.appointmentMenus,
    ["clinicId", "==", clinicId]
  );
  return Menu.fromDocs(docs);
}

export async function readSchedule(
  scheduleId: string
): Promise<AppointmentSchedule> {
  return liteGetDocument(
    liteCollections.appointmentSchedules,
    scheduleId
  ).then(AppointmentSchedule.fromDoc);
}

async function readSchedulesForMenuQuery(
  menuId: string,
  ...extraWheres: [string, WhereFilterOp, unknown][]
): Promise<AppointmentSchedule[]> {
  const [legacyDocs, containsDocs] = await Promise.all([
    liteQueryCollectionWith(
      liteCollections.appointmentSchedules,
      ["resourceId", "==", menuId],
      ...extraWheres
    ),
    liteQueryCollectionWith(
      liteCollections.appointmentSchedules,
      ["resourceId", "array-contains", menuId],
      ...extraWheres
    ),
  ]);
  return AppointmentSchedule.fromDocs([...legacyDocs, ...containsDocs]);
}

export async function readSchedulesForMenu(
  menuId: string
): Promise<AppointmentSchedule[]> {
  return readSchedulesForMenuQuery(menuId);
}

export async function readSchedulesForMenuOnDate(
  menuId: string,
  date: string
): Promise<AppointmentSchedule[]> {
  return readSchedulesForMenuQuery(menuId, ["date", "==", date]);
}

export async function readSchedulesForMenuFromDate(
  menuId: string,
  fromDate: string
): Promise<AppointmentSchedule[]> {
  return readSchedulesForMenuQuery(menuId, ["date", ">=", fromDate]);
}

export async function readScheduleBucketsByClinicIdAndDateRange(
  clinicId: string,
  startDate: string,
  endDate: string
): Promise<AppointmentSchedule[]> {
  const docs = await liteQueryCollectionWith(
    liteCollections.appointmentSchedules,
    ["clinicId", "==", clinicId],
    ["date", ">=", startDate],
    ["date", "<=", endDate]
  );
  return AppointmentSchedule.fromDocs(docs);
}

export async function readScheduleAppointmentsByClinicIdAndDateRange(
  clinicId: string,
  startDate: string,
  endDate: string
): Promise<Appointment[]> {
  const docs = await liteQueryCollectionWith(
    liteCollections.scheduleAppointments,
    ["clinicId", "==", clinicId],
    ["date", ">=", startDate],
    ["date", "<=", endDate]
  );
  return Appointment.fromDocs(docs);
}

export async function readCanceledScheduleAppointmentsByClinicIdAndDateRange(
  clinicId: string,
  startDate: string,
  endDate: string
): Promise<Appointment[]> {
  const docs = await liteQueryCollectionWith(
    liteCollections.scheduleAppointments,
    ["clinicId", "==", clinicId],
    ["status", "==", AppointmentStatus.CANCELED],
    ["date", ">=", startDate],
    ["date", "<=", endDate]
  );
  return Appointment.fromDocs(docs);
}

export async function readScheduleAppointmentsByBucketId(
  bucketId: string
): Promise<Appointment[]> {
  const docs = await liteQueryCollectionWith(
    liteCollections.scheduleAppointments,
    ["bucketId", "==", bucketId]
  );
  return Appointment.fromDocs(docs);
}

export async function readScheduleAppointmentById(
  appointmentId: string
): Promise<Appointment> {
  return liteGetDocument(
    liteCollections.scheduleAppointments,
    appointmentId
  ).then(Appointment.fromDoc);
}
